import { useEffect } from 'react';
import { useAttendeeMessaging } from '@context/AttendeeMessagingContext';
import { SEARCH_ATTENDEES } from '@graphql/operations/attendee/searchAttendees';
import { useLazyQuery } from '@apollo/client';
import { Channel } from 'stream-chat';
import { StreamType } from '@components/chat/shared/useStreamChat';
import { ChannelType } from './constants';

// update attendee data in the context when new channels are loaded.
const useChannelMembers = ({ loadedChannels, limit }: { loadedChannels: Channel<StreamType>[]; limit: number }) => {
  const { updateOtherAttendees, client, getAttendeeData } = useAttendeeMessaging();
  const [getDataOnLoadedAttendees, { data: attendeesData }] = useLazyQuery(SEARCH_ATTENDEES);

  useEffect(() => {
    updateOtherAttendees({
      otherAttendees: attendeesData?.searchAttendees?.data || []
    });
  }, [attendeesData, updateOtherAttendees]);

  // The following use effect needs to run only when loaded channels changes.
  // As the loadedChannels array gets long, following logic finds out which channels are new
  // and make a lazy query based on the new attendee ids of the newly loaded channels.
  // This will only search attendees for 1:1 channels only so we can stay within our limit.
  useEffect(() => {
    const needDataOnAttendeeIds = Object.values(loadedChannels || {}).reduce((channelAcc, channelItem) => {
      if (channelItem?.type === ChannelType.ATTENDEE_MESSAGING) {
        // get the user object of the otherMember
        const [otherMember] = Object.values(channelItem?.state?.members).filter(
          ({ user }) => user.id !== client.userID
        );
        // check if data already exists on otherMembers in attendeeDataOnLoadedChannels
        const dataExists = getAttendeeData(otherMember?.user?.attendeeId);
        // if data does not exist, return this id to make gql call.
        if (!dataExists) {
          channelAcc.push(otherMember?.user?.attendeeId);
        }
      }
      return channelAcc;
    }, []);

    if (needDataOnAttendeeIds.length > 0) {
      getDataOnLoadedAttendees({
        variables: {
          criteria: {
            attendeeIds: needDataOnAttendeeIds,
            pageCriteria: {
              limit
            }
          }
        },
        fetchPolicy: 'cache-and-network'
      });
    }
  }, [loadedChannels]); // eslint-disable-line react-hooks/exhaustive-deps
  // The only time this use effect needs to run is when the loaded channels change.
  // adding other dependancies like state variable `attendeeDataOnLoadedChannels` cause
  // this to make infinite gql calls.
};

export default useChannelMembers;
